import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronUp } from "lucide-react";
import { useLocation } from "react-router";

const AUTH_FLOW_PATHS = new Set([
  "/signin",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/verify-account",
]);

const SHOW_AFTER_PX = 360;

export default function ScrollToTopButton() {
  const { pathname } = useLocation();
  const hiddenRoute = AUTH_FLOW_PATHS.has(pathname);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (hiddenRoute) {
      setVisible(false);
      return undefined;
    }

    const evaluate = () => {
      setVisible(window.scrollY > SHOW_AFTER_PX);
    };

    evaluate();
    window.addEventListener("scroll", evaluate, { passive: true });
    return () => window.removeEventListener("scroll", evaluate);
  }, [pathname, hiddenRoute]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  if (hiddenRoute) return null;

  return (
    <AnimatePresence>
      {visible ? (
        <motion.button
          type="button"
          key="scroll-to-top"
          initial={{ opacity: 0, y: 18, scale: 0.88 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 14, scale: 0.9 }}
          transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          onClick={scrollToTop}
          aria-label="Scroll to top"
          title="Back to top"
          className="group fixed bottom-6 right-5 z-[120] flex h-12 w-12 items-center justify-center rounded-2xl border border-black/10 bg-[#FFD221] text-black shadow-[0_14px_44px_-16px_rgba(0,0,0,0.55)] ring-1 ring-[#c9a000]/25 transition hover:bg-[#ffdf44] hover:shadow-[0_18px_52px_-18px_rgba(0,0,0,0.6)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFD221] sm:bottom-8 sm:right-8 sm:h-[3.25rem] sm:w-[3.25rem]"
        >
          <span
            className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-white/35 via-transparent to-black/[0.06] opacity-0 transition group-hover:opacity-100"
            aria-hidden
          />
          <ChevronUp
            size={22}
            strokeWidth={2.5}
            className="relative transition group-hover:-translate-y-0.5"
            aria-hidden
          />
        </motion.button>
      ) : null}
    </AnimatePresence>
  );
}
